import { useConfigurations } from '@/features/admin/configurations/hooks'
import { useTeams } from '@/features/admin/teams/hooks'
import { useStages } from '@/features/admin/stages/hooks'
import type { ContractConfig, ContractConfigKey } from './contractSourceConfig'

type Props = {
  value: ContractConfig
  onChange: (next: ContractConfig) => void
  disabled?: boolean
}

const PATH_FIELDS: { key: ContractConfigKey; label: string; placeholder: string }[] = [
  { key: 'client_pesel_path', label: 'Ścieżka PESEL klienta *', placeholder: 'client.pesel' },
  { key: 'client_first_name_path', label: 'Ścieżka imienia klienta', placeholder: 'client.firstName' },
  { key: 'client_last_name_path', label: 'Ścieżka nazwiska klienta', placeholder: 'client.lastName' },
  { key: 'second_party_pesel_path', label: 'PESEL drugiej osoby (opc.)', placeholder: 'onDeal.pesel' },
  { key: 'second_party_first_name_path', label: 'Imię drugiej osoby (opc.)', placeholder: 'onDeal.firstName' },
  { key: 'second_party_last_name_path', label: 'Nazwisko drugiej osoby (opc.)', placeholder: 'onDeal.lastName' },
  { key: 'sales_rep_halo_id_path', label: 'Ścieżka Halo ID handlowca', placeholder: 'salesRep.haloUserId' },
  { key: 'external_key_path', label: 'ID pvslides - dedup (opc.)', placeholder: 'presentation.id' },
]

const labelCls = 'text-xs font-medium uppercase tracking-[0.06em] text-slate-600'
const inputCls = 'w-full rounded border border-slate-300 px-2 py-1 text-sm'

/**
 * Pola configu źródła integracji dla `target_entity_type=contract`: produkt/etap/zespół
 * (dropdowny z danych admina) + ścieżki payloadu (dot-paths zależne od pvslides).
 * Współdzielone przez Add i Edit dialog.
 */
export function ContractSourceConfigFields({ value, onChange, disabled }: Props) {
  const productsQ = useConfigurations()
  const teamsQ = useTeams()
  const stagesQ = useStages(value.configuration_code)

  const set = (key: ContractConfigKey, v: string) => onChange({ ...value, [key]: v })

  return (
    <div className="grid gap-3 rounded border border-slate-200 bg-slate-50 p-3">
      <p className="text-xs font-semibold text-slate-700">Konfiguracja intake umowy (contract)</p>

      <div className="grid grid-cols-2 gap-3">
        <label className="grid gap-1">
          <span className={labelCls}>Umowa *</span>
          <select
            value={value.configuration_code}
            onChange={e => onChange({ ...value, configuration_code: e.target.value, land_on_stage: '' })}
            disabled={disabled}
            className={inputCls}
          >
            <option value="">— wybierz —</option>
            {(productsQ.data ?? []).map(p => (
              <option key={p.code} value={p.code}>{p.labelPl} ({p.code})</option>
            ))}
          </select>
        </label>

        <label className="grid gap-1">
          <span className={labelCls}>Etap docelowy *</span>
          <select
            value={value.land_on_stage}
            onChange={e => set('land_on_stage', e.target.value)}
            disabled={disabled || !value.configuration_code}
            className={inputCls}
          >
            <option value="">{value.configuration_code ? '— wybierz —' : '— najpierw umowa —'}</option>
            {(stagesQ.data ?? []).map(s => (
              <option key={s.code} value={s.code}>{s.labelPl} ({s.code})</option>
            ))}
          </select>
        </label>
      </div>

      <label className="grid gap-1">
        <span className={labelCls}>Zespół (auto-assign) *</span>
        <select
          value={value.assign_to_team}
          onChange={e => set('assign_to_team', e.target.value)}
          disabled={disabled}
          className={inputCls}
        >
          <option value="">— wybierz —</option>
          {(teamsQ.data ?? []).map(t => (
            <option key={t.code} value={t.code}>{t.name} ({t.code})</option>
          ))}
        </select>
      </label>

      <div className="mt-1 grid gap-1 border-t border-slate-200 pt-2">
        <p className="text-xs font-semibold text-slate-700">Identyfikacja i routing (NIE pola umowy)</p>
        <p className="text-[11px] text-slate-400">
          Ścieżki w payloadzie do danych, które <strong>sterują logiką</strong>, a nie są polami umowy:
          PESEL/imię → find-or-create klienta i drugiej strony, Halo ID → właściciel umowy, numer umowy → idempotencja.
          Mapowania ~45 <strong>pól umowy</strong> ustawiasz osobno, per pole w Form Builderze (external_keys).
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {PATH_FIELDS.map(f => (
          <label key={f.key} className="grid gap-1">
            <span className={labelCls}>{f.label}</span>
            <input
              value={value[f.key]}
              onChange={e => set(f.key, e.target.value)}
              disabled={disabled}
              className={`${inputCls} font-mono`}
              placeholder={f.placeholder}
            />
          </label>
        ))}
      </div>

      <p className="text-[11px] text-slate-400">Pola z <span className="font-mono">*</span> są wymagane.</p>
    </div>
  )
}
